import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lightbulb, TrendingUp } from 'lucide-react';

interface SmartSearchSuggestionsProps {
  onSuggestionClick: (suggestion: string) => void;
  currentQuery?: string;
}

export const SmartSearchSuggestions: React.FC<SmartSearchSuggestionsProps> = ({ 
  onSuggestionClick,
  currentQuery 
}) => {
  const [trendingSearches, setTrendingSearches] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // Static smart suggestions based on common use cases
  const smartSuggestions = [
    'AI for content writing',
    'Code assistant tools',
    'Image generation bots',
    'Customer service AI',
    'Free AI chatbots',
    'Data analysis tools',
    'Voice AI assistants',
    'Educational AI tutors'
  ];

  // Use case buttons
  const useCaseButtons = [
    { label: 'Writing & Content', query: 'writing assistant content creation' },
    { label: 'Code & Development', query: 'code assistant programming developer' },
    { label: 'Image & Design', query: 'image generation design art' },
    { label: 'Business & Analytics', query: 'business analytics data analysis' },
    { label: 'Education & Learning', query: 'education tutor learning' },
    { label: 'Customer Support', query: 'customer service support chatbot' }
  ];

  useEffect(() => {
    // Generate dynamic suggestions based on current query
    if (currentQuery && currentQuery.length > 2) {
      const queryKeywords = currentQuery.toLowerCase().split(' ');
      const relatedSuggestions = smartSuggestions.filter(suggestion =>
        queryKeywords.some(keyword => 
          suggestion.toLowerCase().includes(keyword) || 
          keyword.length > 3 && suggestion.toLowerCase().includes(keyword.substring(0, 4))
        )
      ).slice(0, 4);
      
      setSuggestions(relatedSuggestions);
    } else {
      setSuggestions(smartSuggestions.slice(0, 6));
    }

    // Simulate trending searches (in a real app, this would come from analytics)
    setTrendingSearches(['ChatGPT alternatives', 'Free AI tools', 'Image generators', 'Code assistants']);
  }, [currentQuery]);

  return (
    <div className="space-y-4">
      {/* Use Case Buttons */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Lightbulb className="h-4 w-4" />
          Browse by Use Case
        </div>
        <div className="flex flex-wrap gap-2">
          {useCaseButtons.map((useCase) => (
            <Button
              key={useCase.label}
              variant="outline"
              size="sm"
              onClick={() => onSuggestionClick(useCase.query)}
              className="text-xs"
            >
              {useCase.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Smart Suggestions */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Lightbulb className="h-4 w-4" />
          {currentQuery ? 'Related Searches' : 'Popular Searches'}
        </div>
        <div className="flex flex-wrap gap-2">
          {suggestions.map((suggestion, index) => (
            <Badge
              key={index}
              variant="secondary"
              className="cursor-pointer hover:bg-accent transition-colors"
              onClick={() => onSuggestionClick(suggestion)}
            >
              {suggestion}
            </Badge>
          ))}
        </div>
      </div>

      {/* Trending Searches */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <TrendingUp className="h-4 w-4" />
          Trending Now
        </div>
        <div className="flex flex-wrap gap-2">
          {trendingSearches.map((trend, index) => (
            <Badge
              key={index}
              variant="outline"
              className="cursor-pointer hover:bg-primary/10 transition-colors"
              onClick={() => onSuggestionClick(trend)}
            >
              {trend}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
};